'use client';

import { cdn } from '@/lib/cdn';
import clsx from 'clsx';
import { Button } from '@/components/ui/Button/Button';
import { useContactInfo } from '@/hooks/useContactInfo';
import { useContactTracking } from '@/hooks/useContactTracking';
import type { CMSContactUs as CMSContactUsType } from '@/lib/cms/types';
import { useTranslations } from 'next-intl';
import { Heading } from '../Heading';

import styles from './ContactUs.module.scss';

// ============================================
// Icons
// ============================================

/**
 * Phone icon
 */
function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

/**
 * Mail icon
 */
function MailIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

// ============================================
// Props Interface - Adapter Output ONLY
// ============================================

export interface ContactUsProps extends Omit<
  CMSContactUsType,
  '__typename' | 'sys' | 'internalName'
> {
  /** Default contact info from config (runtime, not CMS) */
  contactInfo?: {
    phoneNumber?: string;
    email?: string;
    weekdayHours?: string;
    weekendHours?: string;
  };
  /** Translations (runtime, not CMS) */
  labels?: {
    contactUs?: string;
    replyTime?: string;
  };
  /** Optional className */
  className?: string;
  /** CMS index for test IDs */
  cmsIndex?: number;
}

// ============================================
// Main Component
// ============================================

/**
 * ContactUs component
 *
 * Displays contact information with phone and email icons.
 * Supports clicking to initiate calls or emails.
 *
 * Accepts ONLY adapter output (CMSContactUs type) plus runtime config.
 *
 * @example
 * <ContactUs
 *   heading="Need Help?"
 *   description="We're here to assist you"
 *   phone="+1234567890"
 *   email="help@example.com"
 *   cta={{ text: 'Contact Us', url: '/contact' }}
 * />
 */
export function ContactUs({
  type,
  heading,
  description,
  phone,
  email,
  cta,
  backgroundMedia,
  // Runtime props (not from CMS)
  contactInfo: contactInfoProp,
  labels = {},
  className,
  cmsIndex,
}: Readonly<ContactUsProps>) {
  const isSubtle = type === 'Subtle';
  const headingData =
    typeof heading === 'string' ? (heading ? { text: heading } : null) : (heading ?? null);
  const bgImageUrl = backgroundMedia?.url || null;
  const t = useTranslations('contact');

  // Translate contact hours strings (Mon-Fri, Sat-Sun, Closed)
  const translateHours = (hours: string): string => {
    if (!hours) return '';
    return hours
      .replace(/Mon-Fri/gi, t('weekdays'))
      .replace(/Sat-Sun/gi, t('weekends'))
      .replace(/\bClosed\b/gi, t('closed'));
  };

  const { contactUs = t('contactUs'), replyTime = t('replyTime') } = labels;

  // Get contact info from hook (fetched via BFF on page load)
  const hookContactInfo = useContactInfo();
  const { trackPhoneContact, trackEmailContact } = useContactTracking();

  // Priority: CMS values → passed props → hook data (BFF)
  const contactPhone = phone || contactInfoProp?.phoneNumber || hookContactInfo.phoneNumber;
  const contactEmail = email || contactInfoProp?.email || hookContactInfo.email;
  const contactHours = contactInfoProp?.weekdayHours || hookContactInfo.weekdayHours;
  const contactWeekendHours = contactInfoProp?.weekendHours || hookContactInfo.weekendHours;

  const handlePhoneClick = () => {
    if (contactPhone) {
      trackPhoneContact();
      window.location.href = `tel:${contactPhone}`;
    }
  };

  const handleEmailClick = () => {
    if (contactEmail) {
      trackEmailContact();
      window.location.href = `mailto:${contactEmail}`;
    }
  };

  const handleContactClick = () => {
    if (cta?.url) {
      window.location.href = cta.url;
      return;
    }
    if (contactPhone) {
      handlePhoneClick();
    } else if (contactEmail) {
      handleEmailClick();
    }
  };

  const buttonText = cta?.text || contactUs;
  const testIdPrefix = cmsIndex !== undefined ? `contactus-${cmsIndex}` : 'ContactUsContainer';

  return (
    <div className={className} data-testid={testIdPrefix}>
      <div className={clsx(styles.container, isSubtle && styles.containerFullWidth)}>
        <div
          className={clsx(styles.contentWrapper, isSubtle && styles.contentWrapperSubtle)}
          style={
            bgImageUrl
              ? {
                  backgroundImage: `url(${cdn.transformImageUrl(bgImageUrl, { width: 1200, quality: 85, format: 'auto' })})`,
                }
              : undefined
          }
        >
          <div className={styles.content}>
            {headingData?.text && (
              <>
                <Heading
                  Heading={{
                    ...headingData,
                    headingType: headingData.headingType ?? 'h3',
                    headingSeoTag: headingData.headingSeoTag ?? 'h3',
                    mainColor: headingData.mainColor ?? '#ffffff',
                    highlightColor: headingData.highlightColor ?? '#FFC72C',
                  }}
                />
                {description && <p className={styles.subtitle}>{description}</p>}
              </>
            )}

            <div className={styles.contactData}>
              {/* Phone */}
              {contactPhone && (
                <div
                  className={styles.item}
                  onClick={handlePhoneClick}
                  data-testid="ContactUsPhoneItem"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handlePhoneClick()}
                >
                  <div className={styles.iconWrapper}>
                    <PhoneIcon />
                  </div>
                  <span className={styles.title}>{contactPhone}</span>
                  {contactHours && (
                    <span className={styles.hours}>{translateHours(contactHours)}</span>
                  )}
                  {contactWeekendHours && (
                    <span className={styles.hours}>{translateHours(contactWeekendHours)}</span>
                  )}
                </div>
              )}

              {/* Email */}
              {contactEmail && (
                <div
                  className={styles.item}
                  onClick={handleEmailClick}
                  data-testid="ContactUsEmailItem"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleEmailClick()}
                >
                  <div className={styles.iconWrapper}>
                    <MailIcon />
                  </div>
                  <span className={styles.title}>{contactEmail}</span>
                  <span className={styles.hours}>{replyTime}</span>
                </div>
              )}
            </div>

            <div className={styles.buttonContainer}>
              <Button
                variant="primary"
                className={styles.button}
                onClick={handleContactClick}
                data-testid="ContactUsButton"
              >
                {buttonText}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

ContactUs.displayName = 'ContactUs';
